import React, { useState, useEffect } from 'react';

// Import pages
import Landing from './pages/Landing';
import Group from './pages/Group';
import Export from './pages/Export';
import Error from './pages/Error';

// Import context and router
import { GroupProvider } from './features/expenseSplitter/groupContext';
import { router, Route } from './services/router';

/**
 * Main App Component
 * 
 * Handles routing and top-level app state.
 * Uses hash-based routing for static hosting compatibility.
 * 
 * Routes:
 * - / or #/ -> Landing page
 * - #/group/:id -> Group page with URL-encoded state
 * - #/export/:id -> Export page
 * - #/error -> Error page
 */

const App: React.FC = () => {
  const [currentRoute, setCurrentRoute] = useState<Route>({ page: 'landing' });

  // Handle route changes
  useEffect(() => {
    // Set initial route
    setCurrentRoute(router.getCurrentRoute());

    // Listen for route changes
    const unsubscribe = router.addListener((route: Route) => {
      // Validate route
      const validation = router.validateRoute(route);
      if (!validation.valid) {
        console.error('Invalid route:', validation.error);
        router.navigateToError('invalid-route');
        return;
      }
      
      setCurrentRoute(route);
    });

    return unsubscribe;
  }, []);

  const renderCurrentPage = () => {
    switch (currentRoute.page) {
      case 'group':
        return <Group groupId={currentRoute.id} />;
      case 'export':
        return <Export groupId={currentRoute.id} />;
      case 'error':
        return <Error errorCode={currentRoute.params?.code} />;
      default:
        return <Landing />;
    }
  };

  return (
    <GroupProvider>
      <div className="app">
        {renderCurrentPage()}
      </div>
    </GroupProvider>
  );
};

export default App;
